// ---------------------------------------------------------------------------
// Test Mode — simulated projects (8) for Clean Code Culture SRL (CLE-250/253).
//
// Projects bind together clients (clientId), team members (memberIds ->
// allocations) and financials. The same member ids drive the People module's
// allocations so utilization is coherent both ways. Contract values mirror the
// per-client invoiced totals (EUR -> RON) so every project shows an Income /
// Outcome P&L that ties back to the client invoices and the Financials overview
// (CLE-253: ~500K EUR income / 200K+ EUR profit across the company).
// ---------------------------------------------------------------------------

import type {
  ProjectSummary,
  ProjectDetail,
  ProjectStatus,
  ProjectHealth,
  ProjectMember,
  FinancialEntry,
  ResourcesTree,
  TreeFile,
  TreeFolder,
} from '../lib/projectsApi';
import type { ProjectOverview, OverviewMonthly, OverviewMilestone } from '../lib/kbProjectsApi';
import type { Allocation } from '../lib/peopleApi';
import { isoDate, isoDateTime, eur, testToday, EMAIL_DOMAIN } from './company';
import { PEOPLE_REFS } from './people';
import { clientName } from './clients';
import { testCompanyTag } from './companies';

interface ProjectSeed {
  id: string;
  code: string;
  name: string;
  clientId: string;
  status: ProjectStatus;
  health: ProjectHealth;
  contractEur: number;
  memberIds: string[]; // first id is the lead
  startOffset: number; // days from today (negative = past)
  endOffset: number | null;
  // Share of contract recognised as revenue so far (0..1) for the P&L.
  revenueProgress: number;
  description: string;
}

// Contract values mirror the per-client invoiced totals so each project's P&L
// equals the matching client's invoices (CLE-253 cross-entity coherence).
// CLE-255: 6 new projects were added and the originals rebalanced so the sum of
// all 15 contracts still ties out to ~500K EUR.
//   existing: 45 + 45 + 32 + 30 + 20 + 18 + 12 + 10 + 6                 = 218
//   new:      38 (RetuRO) + 32 (FUNCURIER) + 85 (DSU) + 75 (MAE)
//             + 22 (Digital Print) + 30 (Let's Go Travel)               = 282
//   total = 500K EUR.
export const PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: 'pr1',
    code: 'ECOM',
    name: 'E-commerce Platform Redesign',
    clientId: 'c1',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 45_000,
    memberIds: ['p7', 'p9', 'p6', 'p12'],
    startOffset: -90,
    endOffset: 60,
    revenueProgress: 1,
    description: 'Redesign complet al platformei de e-commerce cu checkout nou și CMS headless.',
  },
  {
    id: 'pr2',
    code: 'BANK',
    name: 'Mobile Banking App',
    clientId: 'c2',
    status: 'ACTIVE',
    health: 'AT_RISK',
    contractEur: 45_000,
    memberIds: ['p8', 'p14', 'p10', 'p11', 'p13'],
    startOffset: -75,
    endOffset: 90,
    revenueProgress: 1,
    description: 'Aplicație mobilă de banking cu autentificare biometrică și plăți instant.',
  },
  {
    id: 'pr3',
    code: 'ANLT',
    name: 'SaaS Analytics Dashboard',
    clientId: 'c3',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 32_000,
    memberIds: ['p9', 'p11', 'p6'],
    startOffset: -60,
    endOffset: 45,
    revenueProgress: 1,
    description: 'Dashboard de analiză SaaS cu rapoarte în timp real și export de date.',
  },
  {
    id: 'pr4',
    code: 'CORP',
    name: 'Corporate Website',
    clientId: 'c4',
    status: 'ALMOST_DONE',
    health: 'ON_TRACK',
    contractEur: 30_000,
    memberIds: ['p10', 'p6'],
    startOffset: -45,
    endOffset: 7,
    revenueProgress: 1,
    description: 'Site corporativ multilingv cu CMS și integrare formular de contact.',
  },
  {
    id: 'pr5',
    code: 'APII',
    name: 'API Integration Layer',
    clientId: 'c5',
    status: 'ALMOST_DONE',
    health: 'AT_RISK',
    contractEur: 20_000,
    memberIds: ['p14', 'p12'],
    startOffset: -50,
    endOffset: 10,
    revenueProgress: 1,
    description: 'Strat de integrare API între sistemele de logistică și ERP-ul clientului.',
  },
  {
    id: 'pr6',
    code: 'HRMS',
    name: 'HR Management System',
    clientId: 'c6',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 18_000,
    memberIds: ['p8', 'p11'],
    startOffset: -40,
    endOffset: 50,
    revenueProgress: 1,
    description: 'Sistem de management HR cu pontaj, concedii și evaluări de performanță.',
  },
  {
    id: 'pr7',
    code: 'IOTD',
    name: 'IoT Dashboard',
    clientId: 'c7',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 12_000,
    memberIds: ['p9', 'p13'],
    startOffset: -35,
    endOffset: 40,
    revenueProgress: 1,
    description: 'Dashboard IoT pentru monitorizarea senzorilor din fabrică în timp real.',
  },
  {
    id: 'pr8',
    code: 'MIGR',
    name: 'Legacy Migration',
    clientId: 'c8',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 10_000,
    memberIds: ['p7', 'p15'],
    startOffset: -120,
    endOffset: 30,
    revenueProgress: 1,
    description: 'Migrare sistem legacy către o arhitectură modernă bazată pe microservicii.',
  },
  {
    id: 'pr9',
    code: 'EDUP',
    name: 'EdTech Learning Platform',
    clientId: 'c9',
    status: 'ALMOST_DONE',
    health: 'ON_TRACK',
    contractEur: 6_000,
    memberIds: ['p12', 'p6'],
    startOffset: -55,
    endOffset: 15,
    revenueProgress: 1,
    description: 'Platformă de e-learning cu cursuri video, quiz-uri și certificate.',
  },
  // --- CLE-255: 6 new projects -------------------------------------------
  {
    id: 'pr10',
    code: 'RTRO',
    name: 'RetuRO',
    clientId: 'c13',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 38_000,
    memberIds: ['p3', 'p7', 'p9', 'p6'],
    startOffset: -35,
    endOffset: 75,
    revenueProgress: 1,
    description:
      'Platformă digitală pentru sistemul național de reciclare al României — RetuRO SGR (Sistem de Garanție-Returnare): tracking ambalaje, puncte de colectare, raportare și integrare cu retailerii.',
  },
  {
    id: 'pr11',
    code: 'FCUR',
    name: 'FUNCURIER',
    clientId: 'c14',
    status: 'ACTIVE',
    health: 'AT_RISK',
    contractEur: 32_000,
    memberIds: ['p4', 'p8', 'p10', 'p15'],
    startOffset: -50,
    endOffset: 60,
    revenueProgress: 1,
    description:
      'Platformă de curierat / logistică last-mile cu tracking în timp real, optimizare rute și dispecerizare.',
  },
  {
    id: 'pr12',
    code: 'DSU',
    name: 'DSU — Departamentul pentru Situații de Urgență',
    clientId: 'c15',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 85_000,
    memberIds: ['p2', 'p3', 'p8', 'p14', 'p15'],
    startOffset: -70,
    endOffset: 120,
    revenueProgress: 1,
    description:
      'Sistem digital guvernamental pentru managementul situațiilor de urgență: alertare, coordonare, arhitectură HA cu SLA 99.9% și cerințe stricte de securitate / GDPR.',
  },
  {
    id: 'pr13',
    code: 'MAE',
    name: 'MAE — Portal digital',
    clientId: 'c16',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 75_000,
    memberIds: ['p2', 'p4', 'p7', 'p11', 'p5'],
    startOffset: -60,
    endOffset: 110,
    revenueProgress: 1,
    description:
      'Platformă digitală / portal pentru Ministerul Afacerilor Externe: servicii consulare online, autentificare cu eIDAS și accesibilitate WCAG.',
  },
  {
    id: 'pr14',
    code: 'DPRT',
    name: 'Digital Print',
    clientId: 'c17',
    status: 'ALMOST_DONE',
    health: 'ON_TRACK',
    contractEur: 22_000,
    memberIds: ['p10', 'p6', 'p13'],
    startOffset: -45,
    endOffset: 12,
    revenueProgress: 1,
    description:
      'Platformă e-commerce de management print-on-demand cu configurator produse, generare bon de comandă și integrare plăți.',
  },
  {
    id: 'pr15',
    code: 'LGOT',
    name: "Let's Go Travel",
    clientId: 'c18',
    status: 'ACTIVE',
    health: 'ON_TRACK',
    contractEur: 30_000,
    memberIds: ['p4', 'p9', 'p12', 'p6'],
    startOffset: -42,
    endOffset: 65,
    revenueProgress: 1,
    description:
      'Platformă de rezervări turism / travel agency cu search performant, flux de checkout optimizat și plăți online Stripe.',
  },
];

const CREATED_AT = isoDateTime(-150, 9);
const UPDATED_AT = isoDateTime(-1, 16);

function refName(id: string): string {
  return PEOPLE_REFS.find((p) => p.id === id)?.name ?? 'Necunoscut';
}

export function projectName(id: string): string {
  return PROJECT_SEEDS.find((p) => p.id === id)?.name ?? 'Proiect necunoscut';
}

// Resolve a project by id (pr1) or code (ECOM, case-insensitive) to its id.
// The Overview tab requests `/api/kb/projects/:codeOrId/overview` with the
// project code, so the mock router needs to map either back to the seed id.
export function resolveProjectId(codeOrId: string): string | null {
  const key = codeOrId.toLowerCase();
  const match = PROJECT_SEEDS.find(
    (p) => p.id.toLowerCase() === key || p.code.toLowerCase() === key,
  );
  return match?.id ?? null;
}

function members(seed: ProjectSeed): ProjectMember[] {
  return seed.memberIds.map((mid, i) => ({
    id: `${seed.id}-m-${mid}`,
    role: i === 0 ? 'LEAD' : 'CONTRIBUTOR',
    joinedAt: isoDateTime(seed.startOffset, 9),
    user: {
      id: mid,
      name: refName(mid),
      email: `${refName(mid)
        .toLowerCase()
        .replace(/[^a-z]/g, '.')}@${EMAIL_DOMAIN}`,
      avatarUrl: null,
    },
  }));
}

function summary(seed: ProjectSeed): ProjectSummary {
  const ownerId = seed.memberIds[0] ?? 'p1';
  return {
    id: seed.id,
    code: seed.code,
    name: seed.name,
    description: seed.description,
    status: seed.status,
    health: seed.health,
    startDate: isoDate(seed.startOffset),
    endDate: seed.endOffset != null ? isoDate(seed.endOffset) : null,
    budget: eur(seed.contractEur),
    // The fixtures quote every contract converted to RON (see `eur()`).
    budgetCurrency: 'RON',
    owner: {
      id: ownerId,
      name: refName(ownerId),
      email: `${refName(ownerId)
        .toLowerCase()
        .replace(/[^a-z]/g, '.')}@${EMAIL_DOMAIN}`,
      avatarUrl: null,
    },
    members: members(seed),
    memberCount: seed.memberIds.length,
    // Projects inherit the firm from their client so the demo data stays
    // coherent when switching firms.
    ...testCompanyTag(seed.clientId),
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  };
}

export function buildProjectSummaries(): ProjectSummary[] {
  return PROJECT_SEEDS.map(summary);
}

// Per-client project count for client summaries.
export function projectCountsByClient(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const s of PROJECT_SEEDS) counts[s.clientId] = (counts[s.clientId] ?? 0) + 1;
  return counts;
}

// Allocations per person derived from project membership — feeds People.
export function allocationsByPerson(): Record<string, Allocation[]> {
  const out: Record<string, Allocation[]> = {};
  for (const seed of PROJECT_SEEDS) {
    seed.memberIds.forEach((mid, i) => {
      const list = out[mid] ?? [];
      list.push({
        id: `${seed.id}-alloc-${mid}`,
        projectId: seed.id,
        project: { id: seed.id, name: seed.name, status: seed.status },
        // Lead carries a heavier load; contributors lighter (weekly hours).
        hours: i === 0 ? 24 : Math.max(8, 32 - i * 6),
        startDate: isoDate(seed.startOffset),
        endDate: seed.endOffset != null ? isoDate(seed.endOffset) : null,
      });
      out[mid] = list;
    });
  }
  return out;
}

// Project cost ratio: ~58% of recognised revenue, leaving a ~42% gross margin
// per project. This keeps every project's P&L on the plus side while the
// company-wide net still lands at ~200K EUR after operational overhead.
const PROJECT_COST_RATIO = 0.58;

// --- RetuRO (pr10): rich, multi-month, categorised P&L ---------------------
//
// CLE-258 asks for a fully visual Financials/Overview experience on the RetuRO
// SGR project: revenue vs. expenses across the trailing months, an expense
// breakdown by realistic category (hosting, staging server, subcontractors,
// design, QA, travel, compliance, licences) and cash-flow / trend charts.
//
// The Financials and Overview tabs already derive every chart from
// `financials.entries`, so the cleanest way to light them all up is to seed a
// realistic, deterministic set of monthly invoices + categorised costs here.
// Amounts are stored in RON (eurToRon = 5) like the rest of the dataset.

// One row per trailing month, oldest first, anchored to the test "today" so the
// charts always have 6 points regardless of the calendar month.
function trailingMonths(count: number): { year: number; month: number; offset: number }[] {
  const now = testToday();
  const out: { year: number; month: number; offset: number }[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1, offset: i });
  }
  return out;
}

// Recurring monthly expense categories for RetuRO (EUR/month, per the spec).
const RETURO_EXPENSE_CATEGORIES: { category: string; eurPerMonth: number }[] = [
  { category: 'Server & Hosting', eurPerMonth: 800 },
  { category: 'Server de test (staging)', eurPerMonth: 300 },
  { category: 'Licențe software', eurPerMonth: 200 },
  { category: 'Subcontractori', eurPerMonth: 2_000 },
  { category: 'Design & UX', eurPerMonth: 500 },
  { category: 'QA & Testing', eurPerMonth: 400 },
  { category: 'Deplasări', eurPerMonth: 300 },
  { category: 'Documentație & Compliance', eurPerMonth: 200 },
];

// Milestone invoices issued in specific trailing months (idx from the oldest
// of the 6 trailing months) + an ongoing maintenance retainer every month.
const RETURO_INVOICES: { idx: number; ref: string; label: string; eur: number }[] = [
  { idx: 0, ref: 'RTR-001', label: 'Faza 1: Analiză și arhitectură', eur: 15_000 },
  { idx: 2, ref: 'RTR-002', label: 'Faza 2: MVP Backend API', eur: 25_000 },
  { idx: 4, ref: 'RTR-003', label: 'Faza 3: Frontend + integrări retaileri', eur: 20_000 },
];
const RETURO_MAINTENANCE_EUR = 5_000;

function retuRoFinancialEntries(): FinancialEntry[] {
  const months = trailingMonths(6);
  const entries: FinancialEntry[] = [];

  months.forEach((m, idx) => {
    // Income — milestone invoices on their month…
    for (const inv of RETURO_INVOICES) {
      if (inv.idx !== idx) continue;
      entries.push({
        id: `pr10-fin-rev-${inv.ref}`,
        type: 'REVENUE',
        category: 'Livrabile proiect',
        description: `Factură #${inv.ref} — ${inv.label}`,
        amount: eur(inv.eur),
        currency: 'RON',
        year: m.year,
        month: m.month,
        date: isoDate(-m.offset * 30 - 10),
      });
    }
    // …plus an ongoing monthly maintenance retainer.
    entries.push({
      id: `pr10-fin-rev-maint-${m.year}-${m.month}`,
      type: 'REVENUE',
      category: 'Mentenanță & suport',
      description: 'Mentenanță lunară RetuRO SGR',
      amount: eur(RETURO_MAINTENANCE_EUR),
      currency: 'RON',
      year: m.year,
      month: m.month,
      date: isoDate(-m.offset * 30 - 8),
    });

    // Expenses — one row per category every month so the donut + stacked trend
    // charts have a stable, realistic distribution. Subcontractor & design taper
    // off slightly over time to keep the stacked trend visually interesting.
    for (const cat of RETURO_EXPENSE_CATEGORIES) {
      let eurAmount = cat.eurPerMonth;
      if (cat.category === 'Subcontractori' && idx < 2) eurAmount = 0; // ramped up later
      if (cat.category === 'Design & UX' && idx > 2) eurAmount = Math.round(eurAmount * 0.4);
      if (eurAmount === 0) continue;
      entries.push({
        id: `pr10-fin-cost-${cat.category.replace(/[^a-z]/gi, '')}-${m.year}-${m.month}`,
        type: 'COST',
        category: cat.category,
        description: `${cat.category} — ${m.month}/${m.year}`,
        amount: eur(eurAmount),
        currency: 'RON',
        year: m.year,
        month: m.month,
        date: isoDate(-m.offset * 30 - 5),
      });
    }
  });

  return entries;
}

// --- Single source of truth for a project's P&L ----------------------------
//
// CLE-263: the Overview ("Prezentare generală") and Financials ("Financiar")
// tabs MUST show identical numbers. Both now derive their totals from the SAME
// `financialEntries(seed)` set via this helper, instead of each tab computing
// revenue/cost independently (which made pr10/RetuRO diverge — Financials summed
// the rich per-month entries while Overview used the flat contract value).
interface FinancialSummary {
  totalRevenue: number;
  totalCost: number;
  profit: number;
  profitMargin: number;
  monthly: OverviewMonthly[];
}

function summarizeEntries(entries: FinancialEntry[]): FinancialSummary {
  let totalRevenue = 0;
  let totalCost = 0;
  const map = new Map<string, OverviewMonthly>();
  for (const e of entries) {
    const key = `${e.year}-${String(e.month).padStart(2, '0')}`;
    const row = map.get(key) ?? { month: key, income: 0, outcome: 0 };
    if (e.type === 'REVENUE') {
      totalRevenue += e.amount;
      row.income += e.amount;
    } else {
      totalCost += e.amount;
      row.outcome += e.amount;
    }
    map.set(key, row);
  }
  const profit = totalRevenue - totalCost;
  const profitMargin = totalRevenue > 0 ? Math.round((profit / totalRevenue) * 100) : 0;
  const monthly = [...map.values()].sort((a, b) => a.month.localeCompare(b.month));
  return { totalRevenue, totalCost, profit, profitMargin, monthly };
}

function financialEntries(seed: ProjectSeed): FinancialEntry[] {
  // RetuRO gets the rich, multi-month, categorised P&L (CLE-258).
  if (seed.id === 'pr10') return retuRoFinancialEntries();

  const contractRon = eur(seed.contractEur);
  const revenue = Math.round(contractRon * seed.revenueProgress);
  const cost = Math.round(revenue * PROJECT_COST_RATIO);
  const entries: FinancialEntry[] = [];
  if (revenue > 0) {
    // Spread the recognised revenue/cost across the trailing 6 months so the
    // monthly charts have multiple points. The trailing month absorbs any
    // rounding remainder so the per-entry sums equal `revenue`/`cost` exactly —
    // keeping the Overview and Financials tab totals identical (CLE-263).
    const shares = [0.12, 0.15, 0.16, 0.18, 0.19, 0.2];
    const months = trailingMonths(shares.length);
    let revAcc = 0;
    let costAcc = 0;
    months.forEach((m, idx) => {
      const last = idx === shares.length - 1;
      const revPart = last ? revenue - revAcc : Math.round(revenue * shares[idx]!);
      const costPart = last ? cost - costAcc : Math.round(cost * shares[idx]!);
      revAcc += revPart;
      costAcc += costPart;
      entries.push({
        id: `${seed.id}-fin-rev-${m.year}-${m.month}`,
        type: 'REVENUE',
        category: 'Servicii dezvoltare',
        description: `Venit recunoscut — ${seed.name} (${m.month}/${m.year})`,
        amount: revPart,
        currency: 'RON',
        year: m.year,
        month: m.month,
        date: isoDate(-m.offset * 30 - 10),
      });
      entries.push({
        id: `${seed.id}-fin-cost-${m.year}-${m.month}`,
        type: 'COST',
        category: 'Costuri echipă',
        description: `Cost livrare — ${seed.name} (${m.month}/${m.year})`,
        amount: costPart,
        currency: 'RON',
        year: m.year,
        month: m.month,
        date: isoDate(-m.offset * 30 - 8),
      });
    });
  }
  return entries;
}

export function buildProjectDetail(
  id: string,
  tasks: ProjectDetail['tasks'],
): ProjectDetail | null {
  const seed = PROJECT_SEEDS.find((p) => p.id === id);
  if (!seed) return null;
  const base = summary(seed);
  const entries = financialEntries(seed);
  // Single source of truth: both tabs roll up the SAME entries (CLE-263).
  const { totalRevenue, totalCost, profit, profitMargin } = summarizeEntries(entries);

  const teamMembers = seed.memberIds.map((mid, i) => ({
    id: mid,
    fullName: refName(mid),
    email: `${refName(mid)
      .toLowerCase()
      .replace(/[^a-z]/g, '.')}@${EMAIL_DOMAIN}`,
    title: null,
    department: null,
    avatarUrl: null,
    managerId: null,
    allocationHours: i === 0 ? 24 : Math.max(8, 32 - i * 6),
    isActive: true,
    joinedAt: isoDateTime(seed.startOffset, 9),
  }));

  return {
    ...base,
    team: {
      members: teamMembers,
      orgChart: { roots: [], unassigned: [], total: teamMembers.length },
    },
    financials: {
      budget: eur(seed.contractEur),
      totalRevenue,
      totalCost,
      profit,
      profitMargin,
      entries,
    },
    resources: buildProjectResourcesTree(id).files.map((f) => ({
      id: f.id,
      name: f.name,
      description: f.category,
      fileType: f.fileType,
      fileSize: f.fileSize,
      s3Key: `test/${f.id}`,
      s3Url: null,
      createdAt: f.createdAt ?? CREATED_AT,
    })),
    tasks,
    services: [],
    clientId: seed.clientId,
    client: { id: seed.clientId, name: clientName(seed.clientId) },
  };
}

// --- Project overview (KB aggregation) -------------------------------------
//
// Drives the project detail "Prezentare generală" (Overview) tab via
// GET /api/kb/projects/:codeOrId/overview. Before CLE-253 this route was NOT
// simulated in Test Mode, so the call fell through to the empty fallback
// envelope and the Overview tab crashed on `data.project.status` — which is the
// blank/crash users saw on /projects/pr4 (and every other project, Overview
// being the default tab). This builder returns the full ProjectOverview shape
// so the tab renders, with a per-project monthly P&L that is always on the plus
// side (income > outcome every month).

function overviewMilestones(seed: ProjectSeed): {
  completion: number;
  items: OverviewMilestone[];
} {
  const done = seed.status === 'ALMOST_DONE';
  const items: OverviewMilestone[] = [
    {
      name: 'Kickoff & analiză cerințe',
      status: 'done',
      deadline: isoDate(seed.startOffset + 7),
      progress: 100,
      overdue: false,
    },
    {
      name: 'MVP & demo intern',
      status: 'done',
      deadline: isoDate(seed.startOffset + 30),
      progress: 100,
      overdue: false,
    },
    {
      name: 'UAT & feedback client',
      status: done ? 'done' : 'in_progress',
      deadline:
        seed.endOffset != null ? isoDate(seed.endOffset - 14) : isoDate(seed.startOffset + 60),
      progress: done ? 100 : 65,
      overdue: false,
    },
    {
      name: 'Livrare & go-live',
      status: done ? 'in_progress' : 'todo',
      deadline: seed.endOffset != null ? isoDate(seed.endOffset) : null,
      progress: done ? 80 : 20,
      overdue: false,
    },
  ];
  const completion = Math.round(items.reduce((s, m) => s + m.progress, 0) / items.length);
  return { completion, items };
}

export function buildProjectOverview(
  id: string,
  tasks: { status: string }[] = [],
): ProjectOverview | null {
  const seed = PROJECT_SEEDS.find((p) => p.id === id);
  if (!seed) return null;

  // Single source of truth (CLE-263): derive the P&L from the SAME entries the
  // Financials tab sums, so both tabs always show identical revenue/cost/profit.
  const { totalRevenue, totalCost, profit, monthly } = summarizeEntries(financialEntries(seed));
  const byStatus: Record<string, number> = {};
  for (const t of tasks) byStatus[t.status] = (byStatus[t.status] ?? 0) + 1;
  // Status values are configurable-status slugs (e.g. "done", "cancelled").
  const active = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length;

  return {
    project: {
      id: seed.id,
      code: seed.code,
      name: seed.name,
      status: seed.status,
      health: seed.health,
      client: clientName(seed.clientId),
      isBarter: false,
      startDate: isoDate(seed.startOffset),
      endDate: seed.endOffset != null ? isoDate(seed.endOffset) : null,
      description: seed.description,
      budget: totalRevenue,
      budgetCurrency: 'RON',
    },
    team: {
      total: seed.memberIds.length,
      members: seed.memberIds.map((mid, i) => ({
        email: `${refName(mid)
          .toLowerCase()
          .replace(/[^a-z]/g, '.')}@${EMAIL_DOMAIN}`,
        role: i === 0 ? 'LEAD' : 'CONTRIBUTOR',
        allocationHours: i === 0 ? 24 : Math.max(8, 32 - i * 6),
        startDate: isoDate(seed.startOffset),
        endDate: seed.endOffset != null ? isoDate(seed.endOffset) : null,
      })),
    },
    financials: {
      totalRevenue,
      totalCost,
      profit,
      // Derived from the same totals so the Overview cards stay internally
      // consistent with the revenue/cost shown on both tabs (CLE-263).
      unpaidIncome: Math.round(totalRevenue * 0.15),
      payableOutcome: Math.round(totalCost * 0.1),
      monthly,
    },
    milestones: overviewMilestones(seed),
    tasks: {
      total: tasks.length,
      active,
      overdue: 0,
      byStatus,
    },
    risks:
      seed.health === 'ON_TRACK'
        ? []
        : [
            {
              name: 'Dependență externă întârziată',
              status: 'open',
              impact: 'Posibilă întârziere a livrării finale.',
              action: 'Escaladare către client și plan de contingență.',
              critical: seed.health === 'OFF_TRACK',
            },
          ],
    recentLog: [
      {
        timestamp: isoDateTime(-2, 11),
        message: `Actualizare status pentru ${seed.name}.`,
      },
      {
        timestamp: isoDateTime(-5, 15),
        message: `Factură emisă către ${clientName(seed.clientId)}.`,
      },
    ],
  };
}

// --- Project resources (DMS tree) ------------------------------------------
//
// CLE-255: give every project a small but realistic document tree so the
// Resources tab shows folders + files instead of the empty state. Files are
// grouped under "Contracte", "Specificații", "Design" and "Rapoarte"; a
// downloadUrl of null keeps the preview modal in its graceful "no preview"
// state (Test Mode never serves real binaries).

function resourceFile(
  id: string,
  name: string,
  kind: TreeFile['kind'],
  category: string,
  createdOffset: number,
  fileSize: number,
): TreeFile {
  return {
    type: 'file',
    id,
    name,
    kind,
    fileType:
      kind === 'pdf'
        ? 'application/pdf'
        : kind === 'image'
          ? 'image/png'
          : 'application/octet-stream',
    fileSize,
    createdAt: isoDateTime(createdOffset, 10),
    downloadUrl: null,
    category,
  };
}

function folder(name: string, children: TreeFile[]): TreeFolder {
  return {
    type: 'folder',
    path: `/${name}`,
    name,
    children,
    fileCount: children.length,
  };
}

// Build a deterministic 4-5 document tree for a project.
export function buildProjectResourcesTree(id: string): ResourcesTree {
  const seed = PROJECT_SEEDS.find((p) => p.id === id);
  if (!seed) {
    return {
      tree: { type: 'folder', path: '/', name: 'root', children: [], fileCount: 0 },
      files: [],
    };
  }
  const client = clientName(seed.clientId);
  const files: TreeFile[] = [
    resourceFile(
      `${id}-doc-contract`,
      `Contract ${seed.code} — ${client}.pdf`,
      'pdf',
      'Contracte',
      seed.startOffset - 5,
      184_320,
    ),
    resourceFile(
      `${id}-doc-spec`,
      `Specificații tehnice — ${seed.name}.pdf`,
      'pdf',
      'Specificații',
      seed.startOffset + 3,
      263_680,
    ),
    resourceFile(
      `${id}-doc-mockups`,
      `Design mockups — ${seed.code}.png`,
      'image',
      'Design',
      seed.startOffset + 10,
      1_048_576,
    ),
    resourceFile(
      `${id}-doc-progress`,
      `Raport de progres — ${seed.name}.pdf`,
      'pdf',
      'Rapoarte',
      -7,
      142_336,
    ),
  ];
  // Larger projects get an extra architecture/security document.
  if (seed.contractEur >= 35_000) {
    files.push(
      resourceFile(
        `${id}-doc-arch`,
        `Propunere arhitectură — ${seed.code}.pdf`,
        'pdf',
        'Specificații',
        seed.startOffset + 7,
        321_536,
      ),
    );
  }

  const byCategory = new Map<string, TreeFile[]>();
  for (const f of files) {
    const list = byCategory.get(f.category) ?? [];
    list.push(f);
    byCategory.set(f.category, list);
  }
  const children = [...byCategory.entries()].map(([cat, fs]) => folder(cat, fs));
  const tree: TreeFolder = {
    type: 'folder',
    path: '/',
    name: 'root',
    children,
    fileCount: files.length,
  };
  return { tree, files };
}

// Convenience for the client detail page: the client's projects in the
// ClientProject shape.
export function clientProjects(clientId: string): {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: string;
  health: string | null;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  budgetCurrency: string;
}[] {
  return PROJECT_SEEDS.filter((p) => p.clientId === clientId).map((s) => ({
    id: s.id,
    code: s.code,
    name: s.name,
    description: s.description,
    status: s.status,
    health: s.health,
    startDate: isoDate(s.startOffset),
    endDate: s.endOffset != null ? isoDate(s.endOffset) : null,
    budget: eur(s.contractEur),
    budgetCurrency: 'RON',
  }));
}
